<script lang="ts" setup>
import { ref } from 'vue'
import { useAlert } from '@/libs/alertPortal'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { captureException as newrelicCaptureException } from '@/newrelic'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  useCreateStaffMutation,
  useSettingsPageMemberManagementCreateViewQuery,
  type CreateStaffMutation,
} from './SettingsPageMemberManagementCreateView.generated'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import MemberManagementForm from '@/components/organisms/MemberManagementForm/MemberManagementForm.vue'
import type { MemberManagementFormType } from '@/components/organisms/MemberManagementForm/formSchema'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const viewQuery = useSettingsPageMemberManagementCreateViewQuery()

const isSubmitting = ref(false)
const isCompleted = ref(false)

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'autoChatReplySettingNew') {
    next()
    return
  }
  if (to.name === 'autoChatReplySetting' && !isCompleted.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})

const alert = useAlert()
const router = useRouter()

const createStaffMutation = useCreateStaffMutation()

const handleSubmit = async (form: MemberManagementFormType) => {
  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true

  try {
    const name = form.name === '' ? undefined : form.name

    const data = await createStaffMutation.mutate({
      input: {
        email: form.email,
        name: name,
        role: form.role,
      },
    })

    if (data?.errors || !data?.data) {
      throw new Error('mutation failed')
    }

    defineResponseHandlers<CreateStaffMutation['createStaff'], void>({
      CreateStaffErrorAlreadyExistsPayload: () => {
        alert.error('このメールアドレスは既に登録されています。')
      },
      CreateStaffSuccessPayload: () => {
        alert.success(
          'メンバーを新規作成しました。ログインページのパスワードをお忘れの方へからパスワードの設定をお願いします'
        )

        // eslint-disable-next-line fp/no-mutating-methods
        router.push({
          name: 'memberManagementSetting',
        })
      },
    })(data.data.createStaff)
  } catch (e) {
    isSubmitting.value = false
    console.error(e)
    newrelicCaptureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }

  isSubmitting.value = false
}
</script>

<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>メンバー新規作成</template>
    </SettingsPagePanelHeader>
    <MemberManagementForm
      v-if="viewQuery.result.value"
      :fragment="viewQuery.result.value"
      :is-submitting="isSubmitting"
      @submit="handleSubmit"
    />
  </SettingsPagePanel>
</template>
